import React from "react"
import { toast } from "react-toastify"
import Swal from "sweetalert2"
import { IPersonnelDocumentModel } from "@common-models/*"
import {
     CONST_PERSONNEL_DOCUMENT_TYPE_ATTESTATION_SS,
     CONST_PERSONNEL_DOCUMENT_TYPE_AUTRES,
     CONST_PERSONNEL_DOCUMENT_TYPE_CV,
     CONST_PERSONNEL_DOCUMENT_TYPE_DIPLOME,
     CONST_PERSONNEL_DOCUMENT_TYPE_PASSEPORT_CNI,
     CONST_PERSONNEL_DOCUMENT_TYPE_PERMIS_CONDUIRE,
     CONST_PERSONNEL_DOCUMENT_TYPE_RIB,
     CONST_PERSONNEL_DOCUMENT_TYPE_TITRE_SEJOUR,
} from "@common-constants/*"
import MyGoogleDriveFileViewerInModal from "../../../components/MyDocumentViewer/MyGoogleDriveFileViewerInModal"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import { personnelDEAddDocumentRequest, personnelDEDeleteDocumentRequest, personnelDESetSansDiplomeRequest } from "../core/_requests"
import FournisseurDocumentsSlots from "./_FournisseurDocumentsSlots"

// Type « Contrat commercial » (miroir backend PersonnelDocument::TYPE_CONTRAT_COMMERCIAL) — pas encore dans les constantes communes.
const DOC_TYPE_CONTRAT_COMMERCIAL = "TYPE_CONTRAT_COMMERCIAL"

// Types requis à la soumission côté créateur (le contrat est ajouté ensuite par les RH).
export const REQUIRED_DOCUMENT_TYPES: string[] = [
     CONST_PERSONNEL_DOCUMENT_TYPE_CV,
     CONST_PERSONNEL_DOCUMENT_TYPE_PASSEPORT_CNI,
     CONST_PERSONNEL_DOCUMENT_TYPE_ATTESTATION_SS,
     CONST_PERSONNEL_DOCUMENT_TYPE_RIB,
]

type DocConfig = { type: string; label: string; icon: string; required: boolean; multi: boolean }

const REQUIRED_CONFIG: DocConfig[] = [
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_CV, label: "Curriculum vitae", icon: "fa-file-lines", required: true, multi: false },
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_PASSEPORT_CNI, label: "Passeport ou carte d'identité", icon: "fa-id-card", required: true, multi: false },
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_ATTESTATION_SS, label: "Attestation de Sécurité Sociale", icon: "fa-shield-heart", required: true, multi: false },
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_RIB, label: "Relevé d'Identité Bancaire (RIB)", icon: "fa-building-columns", required: true, multi: false },
]
const TITRE_SEJOUR_CONFIG: DocConfig = { type: CONST_PERSONNEL_DOCUMENT_TYPE_TITRE_SEJOUR, label: "Titre de séjour", icon: "fa-passport", required: true, multi: false }
const OPTIONAL_CONFIG: DocConfig[] = [
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_PERMIS_CONDUIRE, label: "Permis de conduire", icon: "fa-car", required: false, multi: false },
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_AUTRES, label: "Autres documents", icon: "fa-folder-open", required: false, multi: true },
]
// Diplômes (salarié) : obligatoire par défaut, sauf si le collaborateur est déclaré « sans diplôme ».
const DIPLOME_CONFIG: DocConfig = { type: CONST_PERSONNEL_DOCUMENT_TYPE_DIPLOME, label: "Diplômes", icon: "fa-graduation-cap", required: true, multi: true }

// Non salarié : documents rattachés au collaborateur (les documents société vont sur le fournisseur).
const REQUIRED_CONFIG_NON_SALARIE: DocConfig[] = [
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_CV, label: "Curriculum vitae", icon: "fa-file-lines", required: true, multi: false },
     { type: DOC_TYPE_CONTRAT_COMMERCIAL, label: "Contrat commercial", icon: "fa-file-signature", required: true, multi: false },
]
const OPTIONAL_CONFIG_NON_SALARIE: DocConfig[] = [
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_AUTRES, label: "Autres documents", icon: "fa-folder-open", required: false, multi: true },
]

const ACCEPT = "application/pdf, image/*"

// Contrat de travail (salarié) : ajouté par les RH lors de leur validation.
const CONTRAT_CONFIG: DocConfig = { type: "TYPE_CONTRAT", label: "Contrat de travail", icon: "fa-file-contract", required: false, multi: false }

type Props = {
     demandeId: number
     initialDocuments?: IPersonnelDocumentModel[]
     onDocumentsChange?: (documents: IPersonnelDocumentModel[]) => void
     requireTitreSejour?: boolean
     estSalarie?: boolean
     fournisseurId?: number
     canAddContrat?: boolean
     contratRequired?: boolean
     initialSansDiplome?: boolean
}

const DocumentsStep = ({ demandeId, initialDocuments = [], onDocumentsChange, requireTitreSejour = false, estSalarie = true, fournisseurId, canAddContrat = false, contratRequired = false, initialSansDiplome = false }: Props) => {
     const [documents, setDocuments] = React.useState<IPersonnelDocumentModel[]>(initialDocuments.filter(d => !d.isDeleted))
     const [busyType, setBusyType] = React.useState<string | null>(null)
     const [dragOverType, setDragOverType] = React.useState<string | null>(null)
     const [activeDoc, setActiveDoc] = React.useState<IPersonnelDocumentModel | null>(null)
     // « Ce collaborateur n'a pas de diplôme » : rend le document Diplômes facultatif (persisté côté serveur).
     const [sansDiplome, setSansDiplome] = React.useState<boolean>(initialSansDiplome)
     const [sansDiplomeBusy, setSansDiplomeBusy] = React.useState<boolean>(false)
     const replaceInputRefs = React.useRef<Record<number, HTMLInputElement | null>>({})

     // Configuration des documents selon salarié / non salarié.
     const requiredConfig = estSalarie
          ? requireTitreSejour ? [...REQUIRED_CONFIG, TITRE_SEJOUR_CONFIG] : REQUIRED_CONFIG
          : REQUIRED_CONFIG_NON_SALARIE
     const optionalConfig = estSalarie ? OPTIONAL_CONFIG : OPTIONAL_CONFIG_NON_SALARIE
     // Documents requis (pour la progression) : config de base + contrat (créateur RH) + diplômes (salarié, sauf « sans diplôme »).
     const requiredTypes = [
          ...requiredConfig.map(c => c.type),
          ...(contratRequired ? [CONTRAT_CONFIG.type] : []),
          ...(estSalarie && !sansDiplome ? [DIPLOME_CONFIG.type] : []),
     ]

     async function toggleSansDiplome(value: boolean) {
          setSansDiplomeBusy(true)
          const previous = sansDiplome
          setSansDiplome(value) // optimiste
          try {
               await personnelDESetSansDiplomeRequest(demandeId, value)
          } catch (e: any) {
               setSansDiplome(previous)
               toast.error(e?.response?.data?.detail ?? "Échec de l'enregistrement.", { autoClose: false })
          } finally {
               setSansDiplomeBusy(false)
          }
     }
     const providedRequired = requiredTypes.filter(t => documents.some(d => d.type === t)).length

     // Mise à jour fonctionnelle : indispensable car plusieurs uploads peuvent être en vol simultanément
     // (sinon chaque handler repart d'un `documents` figé et écrase l'ajout du précédent).
     function update(updater: (prev: IPersonnelDocumentModel[]) => IPersonnelDocumentModel[]) {
          setDocuments(prev => updater(prev))
     }

     // Notifie le parent à chaque changement (au lieu de le faire dans `update`, pour rester compatible avec les MAJ fonctionnelles).
     React.useEffect(() => {
          onDocumentsChange?.(documents)
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [documents])

     // Synchronise depuis le parent : les documents ajoutés ailleurs (ex. tuiles de préremplissage de l'étape 1)
     // arrivent via `initialDocuments` après le montage — ce composant reste monté (affichage en display:none),
     // donc on réaligne l'état local quand l'ensemble d'ids change (comparaison par ids → pas de boucle).
     React.useEffect(() => {
          const incoming = initialDocuments.filter(d => !d.isDeleted)
          const incomingIds = incoming.map(d => d.id).sort().join(",")
          const localIds = documents.map(d => d.id).sort().join(",")
          if (incomingIds !== localIds) setDocuments(incoming)
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [initialDocuments])

     async function uploadNew(type: string, file: File) {
          setBusyType(type)
          try {
               const r = await personnelDEAddDocumentRequest(demandeId, type, file)
               update(prev => [...prev, r.data as IPersonnelDocumentModel])
               toast.success("Document ajouté.")
          } catch (e: any) {
               toast.error(e?.response?.data?.detail ?? "Échec de l'envoi du document.", { autoClose: false })
          } finally {
               setBusyType(null)
          }
     }

     // Upload de plusieurs fichiers d'un coup (Diplômes / Autres) — séquentiel, tolérant aux échecs.
     async function uploadMany(type: string, files: File[]) {
          setBusyType(type)
          const added: IPersonnelDocumentModel[] = []
          let failures = 0
          for (const file of files) {
               try {
                    const r = await personnelDEAddDocumentRequest(demandeId, type, file)
                    added.push(r.data as IPersonnelDocumentModel)
               } catch {
                    failures++
               }
          }
          if (added.length) update(prev => [...prev, ...added])
          if (added.length) toast.success(`${added.length} document(s) ajouté(s).`)
          if (failures) toast.error(`${failures} document(s) n'ont pas pu être envoyés.`, { autoClose: false })
          setBusyType(null)
     }

     // Remplace : on ajoute le nouveau document puis on supprime l'ancien (évite une perte si l'upload échoue).
     async function replace(oldDoc: IPersonnelDocumentModel, type: string, file: File) {
          setBusyType(type)
          try {
               const r = await personnelDEAddDocumentRequest(demandeId, type, file)
               if (oldDoc.id) await personnelDEDeleteDocumentRequest(demandeId, oldDoc.id)
               update(prev => [...prev.filter(d => d.id !== oldDoc.id), r.data as IPersonnelDocumentModel])
               toast.success("Document remplacé.")
          } catch (e: any) {
               toast.error(e?.response?.data?.detail ?? "Échec du remplacement.", { autoClose: false })
          } finally {
               setBusyType(null)
          }
     }

     function remove(doc: IPersonnelDocumentModel) {
          if (!doc.id) return
          Swal.fire({
               icon: "warning",
               title: "Supprimer ce document ?",
               showCancelButton: true,
               confirmButtonText: "Oui, supprimer",
               cancelButtonText: "Annuler",
               confirmButtonColor: "#d33",
               preConfirm: () =>
                    personnelDEDeleteDocumentRequest(demandeId, doc.id as number)
                         .then(() => update(prev => prev.filter(d => d.id !== doc.id)))
                         .catch(err => Swal.showValidationMessage(err?.response?.data?.detail ?? "Échec de la suppression.")),
          })
     }

     // Ligne d'un document déjà déposé : voir / remplacer / supprimer
     // Rendu inline (fonction appelée, PAS un composant <UploadedRow/>) : évite le démontage/remontage à chaque
     // changement d'état (busyType…) qui interrompait les téléversements concurrents (input file recréé).
     const renderUploadedRow = (doc: IPersonnelDocumentModel, cfg: DocConfig) => (
          <div key={doc.id} className="d-flex align-items-center justify-content-between bg-light-success rounded p-3 mb-2">
               <div className="d-flex align-items-center text-truncate">
                    <i className="fas fa-circle-check text-success fs-4 me-3" />
                    <span className="fw-bold text-truncate" title={doc.nomFichier ?? undefined}>{doc.nomFichier || "Document fourni"}</span>
               </div>
               <div className="d-flex gap-1 flex-shrink-0">
                    <button type="button" className="btn btn-sm btn-icon btn-light-primary" title="Voir" onClick={() => setActiveDoc(doc)}>
                         <i className="fas fa-eye" />
                    </button>
                    <button
                         type="button"
                         className="btn btn-sm btn-icon btn-light-warning"
                         title="Remplacer"
                         disabled={busyType === cfg.type}
                         onClick={() => doc.id && replaceInputRefs.current[doc.id]?.click()}
                    >
                         <i className="fas fa-arrows-rotate" />
                    </button>
                    <button type="button" className="btn btn-sm btn-icon btn-light-danger" title="Supprimer" disabled={busyType === cfg.type} onClick={() => remove(doc)}>
                         <i className="fas fa-trash" />
                    </button>
                    {doc.id && (
                         <input
                              type="file"
                              accept={ACCEPT}
                              className="d-none"
                              ref={el => {
                                   replaceInputRefs.current[doc.id as number] = el
                              }}
                              onChange={e => {
                                   const f = e.target.files?.[0]
                                   if (f) replace(doc, cfg.type, f)
                                   e.target.value = ""
                              }}
                         />
                    )}
               </div>
          </div>
     )

     function handleFiles(cfg: DocConfig, files: File[]) {
          if (!files.length) return
          if (cfg.multi) uploadMany(cfg.type, files)
          else uploadNew(cfg.type, files[0])
     }

     // Zone de dépôt d'un nouveau document — clic OU glisser-déposer, multi-fichiers pour les types « multiple ».
     // Fonction appelée (pas un composant) pour éviter le remontage de l'input file pendant un téléversement.
     const renderUploadZone = (cfg: DocConfig) => {
          const isDragOver = dragOverType === cfg.type
          const base = cfg.required ? "border-warning bg-light-warning" : "border-gray-300"
          return (
               <label
                    className={`d-flex align-items-center justify-content-between rounded p-3 mb-2 cursor-pointer border border-dashed ${isDragOver ? "border-primary bg-light-primary" : base}`}
                    onDragOver={e => e.preventDefault()}
                    onDragEnter={e => {
                         e.preventDefault()
                         setDragOverType(cfg.type)
                    }}
                    onDragLeave={e => {
                         e.preventDefault()
                         setDragOverType(null)
                    }}
                    onDrop={e => {
                         e.preventDefault()
                         setDragOverType(null)
                         handleFiles(cfg, Array.from(e.dataTransfer.files ?? []))
                    }}
               >
                    <div className="d-flex align-items-center text-muted">
                         <i className={`fas ${isDragOver ? "fa-hand-pointer text-primary" : cfg.required ? "fa-circle-exclamation text-warning" : "fa-circle-plus"} fs-4 me-3`} />
                         <span>
                              {busyType === cfg.type
                                   ? "Envoi en cours…"
                                   : isDragOver
                                   ? "Déposez le fichier ici"
                                   : cfg.multi
                                   ? `Glissez-déposez ou cliquez — un ou plusieurs « ${cfg.label} »`
                                   : `Glissez-déposez ou cliquez — « ${cfg.label} »`}
                         </span>
                    </div>
                    <span className="btn btn-sm btn-light-primary">Parcourir</span>
                    <input
                         type="file"
                         accept={ACCEPT}
                         className="d-none"
                         multiple={cfg.multi}
                         disabled={busyType === cfg.type}
                         onChange={e => {
                              handleFiles(cfg, Array.from(e.target.files ?? []))
                              e.target.value = ""
                         }}
                    />
               </label>
          )
     }

     const renderSlot = (cfg: DocConfig) => {
          const matching = documents.filter(d => d.type === cfg.type)
          return (
               <div className="mb-4" key={cfg.type}>
                    <div className="d-flex align-items-center mb-2">
                         <i className={`fas ${cfg.icon} text-gray-600 me-2`} />
                         <span className={`fw-bold ${cfg.required ? "required" : ""}`}>{cfg.label}</span>
                         {cfg.multi && <span className="badge badge-light-secondary ms-2">multiple</span>}
                    </div>
                    {matching.map(doc => renderUploadedRow(doc, cfg))}
                    {/* Slot simple : zone de dépôt si aucun document ; slot multiple : toujours proposer d'ajouter */}
                    {(cfg.multi || matching.length === 0) && renderUploadZone(cfg)}
               </div>
          )
     }

     return (
          <div>
               {/* Progression des documents requis */}
               <div className="d-flex align-items-center justify-content-between mb-6 p-4 bg-light rounded">
                    <span className="fw-bold">Documents requis</span>
                    <span className={`badge fs-7 ${providedRequired === requiredTypes.length ? "badge-light-success" : "badge-light-warning"}`}>
                         {providedRequired} / {requiredTypes.length} fournis
                    </span>
               </div>

               {requiredConfig.map(renderSlot)}

               {/* Contrat de travail : réservé aux RH. Obligatoire dès la soumission quand le créateur est RH (pas d'étape RH). */}
               {canAddContrat && (
                    <>
                         <div className="separator separator-dashed my-6" />
                         <div className="fw-bold text-gray-700 mb-4">
                              Contrat de travail <span className="badge badge-light-primary ms-2">Réservé aux RH</span>
                         </div>
                         {renderSlot({ ...CONTRAT_CONFIG, required: contratRequired })}
                    </>
               )}

               {/* Diplômes : obligatoire par défaut ; case « sans diplôme » pour le rendre facultatif. */}
               {estSalarie && (
                    <>
                         <div className="separator separator-dashed my-6" />
                         <label className="form-check form-check-custom form-check-solid d-flex align-items-center mb-4">
                              <input
                                   type="checkbox"
                                   className="form-check-input me-3"
                                   checked={sansDiplome}
                                   disabled={sansDiplomeBusy}
                                   onChange={e => toggleSansDiplome(e.target.checked)}
                              />
                              <span className="fw-bold text-gray-700">
                                   Ce collaborateur n'a pas de diplôme
                                   {sansDiplomeBusy && <MySimpleSpinner size={"sm"} classNames={"ms-2"} />}
                              </span>
                         </label>
                         {sansDiplome ? (
                              <div className="text-muted fs-7 mb-2">
                                   <i className="fas fa-circle-info me-2" />
                                   Aucun diplôme à fournir pour ce collaborateur.
                              </div>
                         ) : (
                              renderSlot(DIPLOME_CONFIG)
                         )}
                    </>
               )}

               <div className="separator separator-dashed my-6" />
               <div className="fw-bold text-gray-700 mb-4">Documents complémentaires (facultatifs)</div>
               {optionalConfig.map(renderSlot)}

               {/* Non salarié : documents rattachés au fournisseur (KBIS, RC Pro, Vigilance URSSAF, Régularité fiscale, RIB). */}
               {!estSalarie && (
                    <>
                         <div className="separator separator-dashed my-6" />
                         <div className="fw-bold text-gray-700 mb-4">Documents du fournisseur</div>
                         {fournisseurId ? (
                              <FournisseurDocumentsSlots fournisseurId={fournisseurId} />
                         ) : (
                              <div className="alert alert-warning mb-0">Sélectionnez d'abord un fournisseur (étape « Fournisseur ») pour gérer ses documents.</div>
                         )}
                    </>
               )}

               {activeDoc && activeDoc.googleDriveID && (
                    <MyGoogleDriveFileViewerInModal
                         googleDriveID={activeDoc.googleDriveID}
                         googleDriveMimetype={activeDoc.googleDriveMimeType ?? ""}
                         modalTitle={<>{activeDoc.typeLabel ?? "Document"}</>}
                         handleCloseModal={() => setActiveDoc(null)}
                    />
               )}
          </div>
     )
}

export default DocumentsStep
